"use client";

import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useUserInfo } from "~/components/user-info-provider";
import { authClient } from "~/utils/auth-client";
import { buildGameAnalysisEvaluationStorageKey } from "./browserState";
import { isGameAnalysisRole } from "./accessPolicy";

export function useGameAnalysisAuthorization() {
  const session = authClient.useSession();
  const user = useUserInfo();
  const pending =
    session.isPending ||
    ("isRefetching" in session && session.isRefetching === true);
  const resolvedStatus = pending
    ? "pending"
    : session.error
      ? "error"
      : !session.data
        ? "anonymous"
        : user.resolutionStatus !== "resolved"
          ? user.resolutionStatus
          : user.userInfo && isGameAnalysisRole(user.userInfo.role)
            ? "authorized"
            : "forbidden";
  const sessionOwnerId = session.data?.user.id ?? null;
  const sessionRecovery = useRef<{
    ownerId: string | null;
    attempt: object;
    lifetime: object;
    status: "pending" | "error";
  }>();
  if (sessionRecovery.current && (
    sessionRecovery.current.ownerId !== sessionOwnerId ||
    sessionRecovery.current.lifetime !== user.resolutionLifetime ||
    (sessionRecovery.current.status === "error" && pending)
  )) sessionRecovery.current = undefined;
  const invalidated = useRef<{ ownerId: string | null; lifetime: object }>();
  const [, publishInvalidation] = useState(0);
  if (
    invalidated.current &&
    (resolvedStatus !== "authorized" ||
      invalidated.current.ownerId !== sessionOwnerId ||
      invalidated.current.lifetime !== user.resolutionLifetime)
  )
    invalidated.current = undefined;
  const status = sessionRecovery.current?.status ?? (invalidated.current ? "error" : resolvedStatus);
  const ownerId = status === "authorized" ? sessionOwnerId : null;
  const observed = useRef({
    status,
    ownerId,
    lifetime: user.resolutionLifetime,
    generation: {},
  });
  if (
    observed.current.status !== status ||
    observed.current.ownerId !== ownerId ||
    observed.current.lifetime !== user.resolutionLifetime
  ) {
    observed.current = {
      status,
      ownerId,
      lifetime: user.resolutionLifetime,
      generation: {},
    };
  }
  const generation = observed.current.generation;
  const mounted = useRef(true);
  useLayoutEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const isCurrent = useCallback(
    () =>
      mounted.current &&
      observed.current.generation === generation &&
      observed.current.status === "authorized",
    [generation],
  );
  const refreshSession = (resume: boolean) => {
    if (!mounted.current || observed.current.generation !== generation || !session.data) return;
    const attempt = {};
    sessionRecovery.current = {
      ownerId: sessionOwnerId,
      attempt,
      lifetime: user.resolutionLifetime,
      status: "pending",
    };
    observed.current = { ...observed.current, status: "pending", ownerId: null, generation: {} };
    publishInvalidation((version) => version + 1);
    const finish = (success: boolean) => {
      if (!mounted.current || sessionRecovery.current?.attempt !== attempt) return;
      if (success && resume) {
        sessionRecovery.current = undefined;
        user.refetchUserInfo();
      } else {
        // A still-present SDK session does not establish access after server401.
        // Keep an explicit retriable state rather than a blank sign-in component.
        sessionRecovery.current.status = "error";
      }
      publishInvalidation((version) => version + 1);
    };
    void Promise.resolve().then(() => session.refetch()).then(
      () => finish(true),
      () => finish(false),
    );
  };
  return {
    status,
    ownerId,
    // Identity-only UI state also resets when the confirmed account is ineligible.
    identityOwnerId: pending || session.error ? undefined : sessionOwnerId,
    generation,
    cacheStorageKey:
      ownerId === null ? null : buildGameAnalysisEvaluationStorageKey(ownerId),
    allowed: status === "authorized" && ownerId !== null,
    refreshSession: () => refreshSession(false),
    retry: () => {
      if (
        !mounted.current ||
        observed.current.generation !== generation ||
        status !== "error"
      )
        return;
      if (sessionRecovery.current) refreshSession(true);
      else if (session.error)
        void Promise.resolve(session.refetch()).catch(() => undefined);
      else if (session.data) user.refetchUserInfo();
    },
    invalidate: () => {
      if (!isCurrent()) return;
      invalidated.current = {
        ownerId: sessionOwnerId,
        lifetime: user.resolutionLifetime,
      };
      observed.current = {
        ...observed.current,
        status: "error",
        ownerId: null,
        generation: {},
      };
      publishInvalidation((version) => version + 1);
      user.invalidateUserInfo();
    },
    isCurrent,
  };
}
